import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Download, Filter } from 'lucide-react';
import { Modal } from '../components/Modal';
import { ModuleInfoButton } from '../components/ModuleInfoButton';
import LoadingState from '../components/LoadingState';
import Button from '../components/ui/Button';
import SearchInput from '../components/ui/SearchInput';
import { VendorSelect, NO_VENDOR, type VendorSelection } from '../components/VendorSelect';
import { get, postFormData, postJson, API_BASE_URL } from '../lib/apiClient';
import { formatDate as fmtDate, formatDateTime as fmtDateTime } from "../utils/dateFormat";

const PRICE_LIST_INFO_CONTENT = (
  <div className="flex flex-col gap-3">
    <p>The Price List contains items provided by the client.</p>
    <p>It includes item details such as price in TT$ and USD, category and sub category, vendor name, vendor email ID, and the date created.</p>
    <p className="font-medium">From the Price List, you can:</p>
    <ul className="list-disc list-inside space-y-1 pl-1">
      <li>Export the price list</li>
      <li>Select items and share them with a client or vendor</li>
      <li>Upload a new price list</li>
    </ul>
    <p className="font-medium mt-2">Updated Requirements (30/1/26):</p>
    <ul className="list-disc list-inside space-y-1 pl-1">
      <li><strong>Master Catalog:</strong> Items with Cost Price</li>
      <li><strong>Price List:</strong> with Selling Price w/ Markup</li>
      <li>For each client &amp; Vessel different list; Save all List; Add item quantity; Remarks per item</li>
      <li>Choose items; REM SALTIRE as references</li>
      <li>Unit price is only shown in system; Column to add quantity</li>
      <li>Share system link to client, to add quantity &amp; client can edit &amp; see total calculation</li>
      <li>Price in USD; Column for Pack &amp; Unit</li>
    </ul>
  </div>
);

type Vendor = {
  _id: string;
  name: string;
  email: string;
  isActive: boolean;
};

type PriceItem = {
  _id: string;
  vendorId: string;
  rawName?: string;
  normalizedName?: string;
  uom?: string;
  currency?: string;
  price: number;
  lastUpdatedAt?: string;
  needsReview?: boolean;
  vendor?: { _id: string; name: string; email: string } | null;
};

type PriceListDoc = {
  _id: string;
  vendorId?: string;

  // ✅ Support both conventions:
  // - older/newer UI expects parseStatus
  // - backend logs show "status=parsed"
  parseStatus?: 'pending' | 'parsed' | 'failed';
  status?: 'pending' | 'parsed' | 'failed';

  parseError?: string;
  error?: string;

  itemsParsed?: number;
  itemsUpdated?: number;
  parseMeta?: { method?: string; count?: number; [k: string]: any };

  createdAt?: string;
  updatedAt?: string;
};

type VendorsListResponse =
  | { data: Vendor[] }
  | { success: boolean; data: Vendor[] }
  | Vendor[];

type DocsListResponse = | { success: boolean; data: PriceListDoc[] } | { data: PriceListDoc[] };

function normalizeArray<T>(res: any): T[] {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  return [];
}

function unwrapDoc(res: any) {
  if (!res) return null;
  if (res?.data?.data) return res.data.data;
  if (res?.data) return res.data;
  return res;
}

function sortDocsNewestFirst(docs: PriceListDoc[]) {
  return [...docs].sort((a, b) => {
    const ta = new Date(a.updatedAt || a.createdAt || 0).getTime();
    const tb = new Date(b.updatedAt || b.createdAt || 0).getTime();
    return tb - ta;
  });
}

// ✅ Additive helper: normalize doc status across backend/UI variations
function getDocStatus(d: PriceListDoc | null | undefined): 'pending' | 'parsed' | 'failed' {
  const s = (d?.parseStatus || d?.status || 'pending') as any;
  if (s === 'parsed' || s === 'failed' || s === 'pending') return s;
  return 'pending';
}

function getDocError(d: PriceListDoc | null | undefined): string {
  return String(d?.parseError || d?.error || '').trim();
}

/** ✅ Additive: safe PUT JSON helper (keeps existing apiClient untouched) */
async function putJson<T>(path: string, body: unknown): Promise<T> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `Request failed with status ${res.status}`);
  }
  return res.json() as Promise<T>;
}

type SortKey =
  | 'created'
  | 'vendorName'
  | 'vendorEmail'
  | 'itemName'
  | 'currency'
  | 'unitPrice'
  | 'itemPriceUsd'
  | 'taxAdded';

type SortDir = 'asc' | 'desc';
type SortState = { key: SortKey; dir: SortDir } | null;

function clampText(s: any, max = 80) {
  const t = String(s ?? '').trim();
  if (t.length <= max) return t;
  return t.slice(0, max) + '…';
}

function cn(...xs: Array<string | false | null | undefined>) {
  return xs.filter(Boolean).join(' ');
}

const FunnelIcon = ({ className = '' }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h18l-7 8v6l-4 2v-8L3 4z" />
  </svg>
);

/** ✅ Additive small component: inline editable table cell */
function InlineEditCell(props: {
  value: any;
  placeholder?: string;
  kind?: 'text' | 'number';
  disabled?: boolean;
  saving?: boolean;
  onCommit: (nextValue: any) => Promise<void> | void;
}) {
  const { value, placeholder, kind = 'text', disabled, saving, onCommit } = props;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(value === undefined || value === null ? '' : String(value));
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!editing) {
      setDraft(value === undefined || value === null ? '' : String(value));
    }
  }, [value, editing]);

  useEffect(() => {
    if (editing) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [editing]);

  const commit = async () => {
    if (disabled) return;
    const curr = value === undefined || value === null ? '' : String(value);
    const next = String(draft ?? '');
    if (next === curr) {
      setEditing(false);
      return;
    }

    const parsed = kind === 'number' ? Number(next) : next;
    await onCommit(parsed);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => commit()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') setEditing(false);
          }}
          disabled={!!saving}
          className={cn(
            'w-full px-2 py-1 border rounded-md text-sm outline-none',
            'border-slate-200 focus:ring-2 focus:ring-primary-500/30'
          )}
          placeholder={placeholder}
          inputMode={kind === 'number' ? 'decimal' : undefined}
        />
        {saving ? <span className="text-xs text-slate-400">Saving…</span> : null}
      </div>
    );
  }

  const display =
    value === undefined || value === null || String(value).trim() === '' ? (
      <span className="text-slate-400">{placeholder || '—'}</span>
    ) : (
      <span className="text-slate-700">{clampText(value, 120)}</span>
    );

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => setEditing(true)}
      className={cn(
        'w-full text-left',
        disabled ? 'cursor-not-allowed opacity-70' : 'cursor-text',
        'hover:bg-slate-50 rounded px-1 py-0.5'
      )}
      title={disabled ? 'Editing disabled' : 'Click to edit'}
    >
      {display}
    </button>
  );
}

export const PriceListPage: React.FC = () => {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [items, setItems] = useState<PriceItem[]>([]);
  const [loading, setLoading] = useState(false);

  const [filterOpen, setFilterOpen] = useState(false);
  // The Filter's vendor is the ONE supplier picker (D107.3); upload and URL ingest send its id.
  const [vendorFilter, setVendorFilter] = useState<VendorSelection>(NO_VENDOR);
  const vendorId = vendorFilter.vendorId ?? '';
  const [search, setSearch] = useState<string>('');
  const [needsReview, setNeedsReview] = useState<'all' | 'true' | 'false'>('all');

  const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>({});

  const [sendOpen, setSendOpen] = useState(false);
  const [sendTo, setSendTo] = useState('');
  const [sendCc, setSendCc] = useState('');
  const [sendVendor, setSendVendor] = useState<VendorSelection>(NO_VENDOR);
  const [sendContact, setSendContact] = useState('');
  const [sendSubject, setSendSubject] = useState('');
  const [sendMessage, setSendMessage] = useState('');

  const [uploading, setUploading] = useState(false);

  // ✅ Docs UI + last upload status
  const [docsOpen, setDocsOpen] = useState(false);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docs, setDocs] = useState<PriceListDoc[]>([]);
  const [lastUploadDocId, setLastUploadDocId] = useState<string>('');
  const [lastUploadStatus, setLastUploadStatus] = useState<
    | null
    | { kind: 'pending'; text: string }
    | { kind: 'success'; text: string }
    | { kind: 'error'; text: string }
  >(null);

  // ✅ ADDITIVE: Flipbook URL modal
  const [urlOpen, setUrlOpen] = useState(false);
  const [flipbookUrl, setFlipbookUrl] = useState('');
  const [flipbookSubject, setFlipbookSubject] = useState('Price List (Link)');
  const [urlSubmitting, setUrlSubmitting] = useState(false);

  // ✅ ADDITIVE: top search (same "search" state) + column sort
  const [topSearch, setTopSearch] = useState<string>(''); // UI input
  const [sortState, setSortState] = useState<SortState>(null);

  // ✅ ADDITIVE: row-level saving indicators for inline edit
  const [savingById, setSavingById] = useState<Record<string, boolean>>({});

  const selectedItemIds = useMemo(
    () => Object.keys(selectedIds).filter((id) => selectedIds[id]),
    [selectedIds]
  );

  const allSelected = useMemo(() => {
    if (items.length === 0) return false;
    return items.every((it) => selectedIds[it._id]);
  }, [items, selectedIds]);

  const toggleAll = () => {
    if (!items.length) return;
    if (allSelected) setSelectedIds({});
    else {
      const next: Record<string, boolean> = {};
      items.forEach((it) => (next[it._id] = true));
      setSelectedIds(next);
    }
  };

  const toggleOne = (id: string) => {
    setSelectedIds((p) => ({ ...p, [id]: !p[id] }));
  };

  // Read only to fill the Send modal's "To" address: a picker entry carries no email.
  const fetchVendors = async () => {
    try {
      const res = await get<VendorsListResponse>('/vendors');
      setVendors(normalizeArray<Vendor>(res));
    } catch (e) {
      console.error('Failed to load vendors', e);
      setVendors([]);
    }
  };

  /** ✅ ADDITIVE: map UI sort to backend sort/order when supported */
  function getBackendSortParams(s: SortState): { sort?: string; order?: 'asc' | 'desc' } {
    if (!s) return {};
    // backend allows: lastUpdatedAt, price, normalizedName
    if (s.key === 'created') return { sort: 'lastUpdatedAt', order: s.dir };
    if (s.key === 'unitPrice' || s.key === 'itemPriceUsd') return { sort: 'price', order: s.dir };
    if (s.key === 'itemName') return { sort: 'normalizedName', order: s.dir };
    return {}; // unsupported -> do client-side only
  }

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (vendorId) params.set('vendorId', vendorId);

      // Use the "search" state (synced from topSearch/filter modal)
      if (search.trim()) params.set('search', search.trim());
      if (needsReview !== 'all') params.set('needsReview', needsReview);

      // ✅ Sorting (server when possible)
      const b = getBackendSortParams(sortState);
      if (b.sort) params.set('sort', b.sort);
      if (b.order) params.set('order', b.order);

      // ✅ IMPORTANT: increase limit so you can see >50 items
      params.set('page', '1');
      params.set('limit', '500');

      params.set('_t', String(Date.now()));

      const qs = params.toString();
      const res: any = await get<any>(`/vendor-price-list/items${qs ? `?${qs}` : ''}`);

      const rows =
        (Array.isArray(res) ? res : null) ||
        (Array.isArray(res?.data) ? res.data : null) ||
        (Array.isArray(res?.data?.data) ? res.data.data : null) ||
        (Array.isArray(res?.data?.data?.data) ? res.data.data.data : null) ||
        (Array.isArray(res?.data?.success?.data) ? res.data.success.data : null) ||
        [];

      setItems(rows);
      setSelectedIds({});
    } catch (e) {
      console.error('Failed to load price items', e);
      setItems([]);
      setSelectedIds({});
    } finally {
      setLoading(false);
    }
  };

  // ✅ ADDITIVE: client-side sort fallback for columns the backend doesn’t support
  const displayItems = useMemo(() => {
    if (!sortState) return items;

    const key = sortState.key;
    const dir = sortState.dir === 'asc' ? 1 : -1;

    // If backend already sorted for these, we still keep stable behavior — but sorting again is okay.
    const sorted = [...items].sort((a, b) => {
      const av = a as any;
      const bv = b as any;

      const str = (x: any) => String(x ?? '').toLowerCase();
      const num = (x: any) => {
        const n = Number(x);
        return Number.isFinite(n) ? n : 0;
      };
      const dateMs = (x: any) => new Date(x || 0).getTime();

      if (key === 'created') return (dateMs(a.lastUpdatedAt) - dateMs(b.lastUpdatedAt)) * dir;
      if (key === 'vendorName') return str(a.vendor?.name).localeCompare(str(b.vendor?.name)) * dir;
      if (key === 'vendorEmail') return str(a.vendor?.email).localeCompare(str(b.vendor?.email)) * dir;
      if (key === 'itemName') return str(a.rawName || a.normalizedName).localeCompare(str(b.rawName || b.normalizedName)) * dir;
      if (key === 'currency') return str(a.currency).localeCompare(str(b.currency)) * dir;
      if (key === 'unitPrice' || key === 'itemPriceUsd') return (num(a.price) - num(b.price)) * dir;

      // taxAdded is currently static "No" in UI; keep stable
      return 0;
    });

    return sorted;
  }, [items, sortState]);

  const fetchDocs = async (opts?: { bustCache?: boolean }): Promise<PriceListDoc[]> => {
    try {
      const params = new URLSearchParams();
      if (vendorId) params.set('vendorId', vendorId);
      if (opts?.bustCache) params.set('_t', String(Date.now()));

      const qs = params.toString();
      const res = await get<DocsListResponse>(`/vendor-price-list/documents${qs ? `?${qs}` : ''}`);
      return sortDocsNewestFirst(normalizeArray<PriceListDoc>(res));
    } catch (e) {
      console.error('Failed to load price list docs', e);
      return [];
    }
  };

  const refreshDocsList = async () => {
    setDocsLoading(true);
    try {
      const rows = await fetchDocs({ bustCache: true });
      setDocs(rows);
    } finally {
      setDocsLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ ADDITIVE: keep top search UI + shared search state in sync
  useEffect(() => {
    setTopSearch(search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ ADDITIVE: debounce top search (doesn't disrupt Filter modal)
  const debounceRef = useRef<number | null>(null);
  useEffect(() => {
    // keep shared "search" updated from topSearch
    setSearch(topSearch);

    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      fetchItems();
    }, 350);

    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topSearch, vendorId, needsReview, sortState]);

  const onApplyFilters = () => {
    setFilterOpen(false);
    fetchItems();
  };

  const exportExcel = async () => {
    if (selectedItemIds.length === 0) {
      alert('Select at least 1 item to export.');
      return;
    }

    const res = await fetch(`${API_BASE_URL}/vendor-price-list/export/excel`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(localStorage.getItem('token')
          ? { Authorization: `Bearer ${localStorage.getItem('token')}` }
          : {}),
      },
      body: JSON.stringify({ itemIds: selectedItemIds }),
    });

    if (!res.ok) {
      const t = await res.text().catch(() => '');
      alert(t || `Export failed (${res.status})`);
      return;
    }

    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'price_items.xlsx';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportPdf = async () => {
    if (selectedItemIds.length === 0) {
      alert('Select at least 1 item to export.');
      return;
    }

    const res = await fetch(`${API_BASE_URL}/vendor-price-list/export/pdf`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(localStorage.getItem('token')
          ? { Authorization: `Bearer ${localStorage.getItem('token')}` }
          : {}),
      },
      body: JSON.stringify({ itemIds: selectedItemIds }),
    });

    if (!res.ok) {
      const t = await res.text().catch(() => '');
      alert(t || `Export failed (${res.status})`);
      return;
    }

    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'price_items.pdf';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  async function pollUntilParsed(latestDocId?: string) {
    const start = Date.now();
    const timeoutMs = 180000;
    const intervalMs = 2000;

    setLastUploadStatus({ kind: 'pending', text: 'Uploaded. Parsing in progress…' });

    while (Date.now() - start < timeoutMs) {
      const rows = await fetchDocs({ bustCache: true });

      const target =
        (latestDocId ? rows.find((d) => d._id === latestDocId) : null) || rows[0] || null;

      if (!target) {
        await new Promise((r) => setTimeout(r, intervalMs));
        continue;
      }

      const st = getDocStatus(target);

      if (st === 'parsed') {
        setLastUploadStatus({
          kind: 'success',
          text: `Parsed ✅ (method: ${target.parseMeta?.method || 'n/a'}, parsed: ${target.itemsParsed ?? 0})`,
        });
        await fetchItems();
        await refreshDocsList();
        return;
      }

      if (st === 'failed') {
        setLastUploadStatus({
          kind: 'error',
          text: `Parse Failed ❌ ${getDocError(target) || 'Unknown error'}`,
        });
        await refreshDocsList();
        return;
      }

      await new Promise((r) => setTimeout(r, intervalMs));
    }

    setLastUploadStatus({
      kind: 'pending',
      text: 'Parsing is still running. Open Documents to see status.',
    });
    await fetchItems();
    await refreshDocsList();
  }

  const onUploadFile = async (file: File) => {
    if (!vendorId) {
      alert('Select a vendor in Filter first (vendorId is required for manual upload).');
      return;
    }
    setUploading(true);
    setLastUploadDocId('');
    try {
      const fd = new FormData();
      fd.append('vendorId', vendorId);
      fd.append('file', file);

      const res: any = await postFormData('/vendor-price-list/documents/manual-upload-v2', fd);
      const doc = unwrapDoc(res) as PriceListDoc | null;

      if (!doc?._id) {
        setLastUploadStatus({ kind: 'pending', text: 'Uploaded. Parsing pending…' });
        return;
      }

      setLastUploadDocId(doc._id);

      const st = getDocStatus(doc);

      if (st === 'parsed') {
        setLastUploadStatus({
          kind: 'success',
          text: `Uploaded & Parsed ✅ (method: ${doc.parseMeta?.method || 'n/a'}, parsed: ${doc.itemsParsed ?? 0})`,
        });
        await fetchItems();
        await refreshDocsList();
        return;
      }

      if (st === 'failed') {
        setLastUploadStatus({
          kind: 'error',
          text: `Uploaded but Parse Failed ❌ ${getDocError(doc) || 'Unknown error'}`,
        });
        await refreshDocsList();
        return;
      }

      await pollUntilParsed(doc._id);
    } catch (e: any) {
      console.error(e);
      setLastUploadStatus({ kind: 'error', text: e?.message || 'Upload failed' });
      alert(e?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const onSubmitFlipbookUrl = async () => {
    if (!vendorId) {
      alert('Select a vendor in Filter first (vendorId is required for URL ingestion).');
      return;
    }

    const url = flipbookUrl.trim();
    if (!url) {
      alert('Paste a Flipbook/Portal URL.');
      return;
    }

    setUrlSubmitting(true);
    setLastUploadDocId('');
    try {
      const res: any = await postJson('/vendor-price-list/documents/flipbook-url-v2', {
        vendorId,
        linkUrl: url,
        subject: flipbookSubject || 'Price List (Link)',
      });

      const doc = unwrapDoc(res) as PriceListDoc | null;

      if (!doc?._id) {
        setLastUploadStatus({ kind: 'pending', text: 'Submitted URL. Parsing pending…' });
        setUrlOpen(false);
        return;
      }

      setLastUploadDocId(doc._id);
      setUrlOpen(false);

      const st = getDocStatus(doc);

      if (st === 'parsed') {
        setLastUploadStatus({
          kind: 'success',
          text: `URL Parsed ✅ (method: ${doc.parseMeta?.method || 'n/a'}, parsed: ${doc.itemsParsed ?? 0})`,
        });
        await fetchItems();
        await refreshDocsList();
        return;
      }

      if (st === 'failed') {
        setLastUploadStatus({
          kind: 'error',
          text: `URL Parse Failed ❌ ${getDocError(doc) || 'Unknown error'}`,
        });
        await refreshDocsList();
        return;
      }

      setLastUploadStatus({ kind: 'pending', text: 'URL submitted. Parsing in progress…' });
      await pollUntilParsed(doc._id);
    } catch (e: any) {
      console.error(e);
      setLastUploadStatus({ kind: 'error', text: e?.message || 'URL submit failed' });
      alert(e?.message || 'URL submit failed');
    } finally {
      setUrlSubmitting(false);
    }
  };

  const openSend = () => {
    const v = vendors.find((x) => x._id === vendorId);
    setSendVendor(v ? { vendorId: v._id, name: v.name } : NO_VENDOR);
    setSendTo(v?.email || '');
    setSendSubject('Price List');
    setSendOpen(true);
  };

  const sendPriceList = async () => {
    alert('Send Price List is UI-only right now. Next step is backend endpoint to email exports.');
    setSendOpen(false);
  };

  const openDocs = async () => {
    setDocsOpen(true);
    await refreshDocsList();
  };

  const statusPill = (d: PriceListDoc) => {
    const s = getDocStatus(d);
    const base = 'inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold';
    if (s === 'parsed') return <span className={`${base} bg-cyan-50 text-cyan-700`}>Parsed</span>;
    if (s === 'failed') return <span className={`${base} bg-rose-50 text-rose-700`}>Failed</span>;
    return <span className={`${base} bg-amber-50 text-amber-700`}>Pending</span>;
  };

  /** ✅ ADDITIVE: header click sorting */
  const toggleSort = (key: SortKey) => {
    setSortState((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return null; // cycle back to none
    });
  };

  const sortIcon = (key: SortKey) => {
    if (!sortState || sortState.key !== key) {
      return <span className="text-slate-300 text-xs leading-none">↕</span>;
    }
    return sortState.dir === 'asc' ? (
      <span className="text-slate-700 text-xs leading-none">▲</span>
    ) : (
      <span className="text-slate-700 text-xs leading-none">▼</span>
    );
  };

  const thButton = (label: string, key: SortKey) => (
    <button
      type="button"
      onClick={() => toggleSort(key)}
      className="inline-flex items-center gap-1.5 hover:text-slate-800"
      title="Sort"
    >
      <span>{label}</span>
      <FunnelIcon className="w-3.5 h-3.5 text-slate-500" />
      {sortIcon(key)}
    </button>
  );

  /** ✅ ADDITIVE: inline save handler */
  const saveItemField = async (id: string, patch: Partial<PriceItem>) => {
    setSavingById((p) => ({ ...p, [id]: true }));

    // optimistic update
    setItems((prev) => prev.map((it) => (it._id === id ? { ...it, ...patch } : it)));

    try {
      await putJson<any>(`/vendor-price-list/items/${id}`, patch);
      // keep lastUpdatedAt updated in UI (backend sets it)
      // best-effort refresh: only update that row's timestamp locally
      setItems((prev) =>
        prev.map((it) =>
          it._id === id ? { ...it, lastUpdatedAt: new Date().toISOString() } : it
        )
      );
    } catch (e: any) {
      console.error('Save failed', e);
      alert(e?.message || 'Save failed');
      // rollback by refetch (safe)
      await fetchItems();
    } finally {
      setSavingById((p) => {
        const next = { ...p };
        delete next[id];
        return next;
      });
    }
  };

  return (
    <div className="w-full h-full min-h-0 flex flex-col">
      {/* The app's header band: same gutter, same 20px title, same controls.
          It stays sticky and keeps the backdrop, because this page scrolls a
          very long grid under it. */}
      <div className="sticky top-0 z-10 border-b border-slate-200 bg-brand-bg/95 backdrop-blur">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 pb-3 pt-4 sm:px-8">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 shrink-0">
              <h1 className="text-xl font-semibold leading-7 text-slate-900">Price list</h1>
              <ModuleInfoButton content={PRICE_LIST_INFO_CONTENT} ariaLabel="Price List information" />
            </div>

            <div className="hidden md:flex items-center">
              <SearchInput
                value={topSearch}
                onChange={setTopSearch}
                placeholder="Search item / uom / vendor…"
                className="w-[360px]"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={() => setFilterOpen(true)} icon={<Filter aria-hidden="true" className="h-4 w-4" />}>
              Filter
            </Button>

            <Button onClick={openDocs}>
              Documents
            </Button>

            <Button onClick={() => setUrlOpen(true)} title="Add a Flipbook/Portal URL to parse">
              Add URL
            </Button>

            <Button onClick={exportExcel} icon={<Download aria-hidden="true" className="h-4 w-4" />}>
              Export
            </Button>

            <Button onClick={openSend}>
              Send
            </Button>

            {/* A file input has to stay a <label>, so this matches Button's
                primary variant by hand rather than wrapping one. */}
            <label className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-md border border-primary-500 bg-primary-500 px-4 text-sm font-semibold text-white hover:border-primary-600 hover:bg-primary-600">
              {uploading ? 'Uploading…' : 'Upload'}
              <input
                type="file"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onUploadFile(f);
                  e.currentTarget.value = '';
                }}
              />
            </label>
          </div>
        </div>

        {/* Small hint row (mobile search) */}
        <div className="px-4 pb-3 sm:px-8 md:hidden">
          <SearchInput
            value={topSearch}
            onChange={setTopSearch}
            placeholder="Search item / uom / vendor…"
            className="w-full"
          />
        </div>

        {lastUploadStatus ? (
          <div
            className={`mx-3 mb-3 px-3 py-2 rounded-md border text-sm ${
              lastUploadStatus.kind === 'success'
                ? 'bg-cyan-50 border-cyan-200 text-cyan-800'
                : lastUploadStatus.kind === 'error'
                ? 'bg-rose-50 border-rose-200 text-rose-800'
                : 'bg-amber-50 border-amber-200 text-amber-800'
            }`}
          >
            {lastUploadStatus.text}
            {lastUploadDocId ? (
              <span className="ml-2 text-xs opacity-80">(doc: {lastUploadDocId})</span>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="flex-1 min-h-0 p-3 overflow-auto">
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="w-full h-[calc(100vh-220px)] min-h-[320px] overflow-auto">
            <table className="data-table min-w-[1200px] w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
                <tr className="text-left text-slate-600">
                  <th className="px-3 py-3 w-[44px] bg-slate-50">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                  </th>

                  <th className="px-4 py-3 bg-slate-50">{thButton('Created', 'created')}</th>
                  <th className="px-4 py-3 bg-slate-50">{thButton('Vendor Name', 'vendorName')}</th>
                  <th className="px-4 py-3 bg-slate-50">{thButton('Vendor Email id', 'vendorEmail')}</th>
                  <th className="px-4 py-3 bg-slate-50">{thButton('Item Name', 'itemName')}</th>
                  <th className="px-4 py-3 bg-slate-50">{thButton('Currency', 'currency')}</th>
                  <th className="px-4 py-3 bg-slate-50">{thButton('Unit Price', 'unitPrice')}</th>
                  <th className="px-4 py-3 bg-slate-50">{thButton('Item Price', 'itemPriceUsd')}</th>
                  <th className="px-4 py-3 bg-slate-50">Tax added</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td className="px-4 py-6 text-slate-500" colSpan={9}>
                      <LoadingState text="Loading price list..." />
                    </td>
                  </tr>
                ) : displayItems.length === 0 ? (
                  <tr>
                    <td className="px-4 py-6 text-slate-500" colSpan={9}>
                      No price list items found.
                    </td>
                  </tr>
                ) : (
                  displayItems.map((it) => {
                    const saving = !!savingById[it._id];
                    return (
                      <tr key={it._id} className="border-b border-slate-100 hover:bg-slate-50/40">
                        <td className="px-3 py-3">
                          <input
                            type="checkbox"
                            checked={!!selectedIds[it._id]}
                            onChange={() => toggleOne(it._id)}
                          />
                        </td>

                        <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                          {fmtDate(it.lastUpdatedAt, '-')}
                          {saving ? <span className="ml-2 text-xs text-slate-400">Saving…</span> : null}
                        </td>

                        <td className="px-4 py-3 text-slate-800 font-medium whitespace-nowrap">
                          {it.vendor?.name || ''}
                        </td>

                        <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{it.vendor?.email || ''}</td>

                        {/* ✅ Editable: Item Name (rawName) */}
                        <td className="px-4 py-3">
                          <InlineEditCell
                            value={it.rawName || ''}
                            placeholder="Item name"
                            saving={saving}
                            onCommit={async (v) => {
                              const next = String(v ?? '').trim();
                              if (!next) return;
                              await saveItemField(it._id, { rawName: next });
                            }}
                          />
                          {/* show normalized below (read-only label) */}
                          <div className="text-xs text-slate-400 mt-0.5">
                            normalized: {it.normalizedName || '—'}
                          </div>
                        </td>

                        {/* ✅ Editable: Currency */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <InlineEditCell
                            value={(it.currency || 'USD').toUpperCase()}
                            placeholder="USD"
                            saving={saving}
                            onCommit={async (v) => {
                              const next = String(v ?? '').trim().toUpperCase();
                              if (!next) return;
                              await saveItemField(it._id, { currency: next });
                            }}
                          />
                        </td>

                        {/* ✅ Editable: Unit Price */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <InlineEditCell
                            kind="number"
                            value={Number(it.price ?? 0).toFixed(2)}
                            placeholder="0.00"
                            saving={saving}
                            onCommit={async (v) => {
                              const n = Number(v);
                              if (!Number.isFinite(n)) return;
                              await saveItemField(it._id, { price: n });
                            }}
                          />
                        </td>

                        {/* Item Price (USD) (same as price for now) */}
                        <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                          {Number(it.price ?? 0).toFixed(2)}
                        </td>

                        <td className="px-4 py-3 text-slate-700 whitespace-nowrap">No</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* ✅ tiny footer hint */}
          <div className="px-3 py-2 border-t border-slate-200 text-xs text-slate-500">
            Tip: Click a cell (Item Name / Currency / Unit Price) to edit. Changes auto-save on blur/Enter.
          </div>
        </div>
      </div>

      {/* Filter */}
      <Modal isOpen={filterOpen} title="Filter" onClose={() => setFilterOpen(false)} maxWidthClass="max-w-xl">
        <div className="space-y-4">
          <VendorSelect label="Vendor" placeholder="All vendors" value={vendorFilter} onChange={setVendorFilter} />

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Search</label>
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setTopSearch(e.target.value); // keep top search synced
              }}
              placeholder="Search item / uom / vendor"
              className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm outline-none focus:ring-2 focus:ring-primary-500/30"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Needs Review</label>
            <select
              value={needsReview}
              onChange={(e) => setNeedsReview(e.target.value as any)}
              className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white outline-none focus:ring-2 focus:ring-primary-500/30"
            >
              <option value="all">All</option>
              <option value="true">Needs Review</option>
              <option value="false">Reviewed</option>
            </select>
          </div>

          <div className="pt-2 flex justify-end gap-2 border-t border-slate-200">
            <button
              onClick={() => setFilterOpen(false)}
              className="px-4 py-2 rounded-md border border-slate-200 bg-white text-sm hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={onApplyFilters}
              className="px-4 py-2 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600"
            >
              Apply
            </button>
          </div>
        </div>
      </Modal>

      {/* Documents */}
      <Modal isOpen={docsOpen} title="Documents" onClose={() => setDocsOpen(false)} maxWidthClass="max-w-3xl">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-xs text-slate-500">
              Showing latest documents{vendorId ? ' for selected vendor' : ''}.
            </div>
            <button
              onClick={refreshDocsList}
              className="px-3 py-2 rounded-md border border-slate-200 bg-white text-sm hover:bg-slate-50"
            >
              Refresh
            </button>
          </div>

          {docsLoading ? (
            <div className="text-sm text-slate-500 py-6">Loading documents…</div>
          ) : docs.length === 0 ? (
            <div className="text-sm text-slate-500 py-6">No documents found.</div>
          ) : (
            <div className="border border-slate-200 rounded-md overflow-hidden">
              <div className="max-h-[60vh] overflow-auto">
                <table className="data-table min-w-[900px] w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
                    <tr className="text-left text-slate-600">
                      <th className="px-4 py-3 bg-slate-50">Created</th>
                      <th className="px-4 py-3 bg-slate-50">Status</th>
                      <th className="px-4 py-3 bg-slate-50">Parsed</th>
                      <th className="px-4 py-3 bg-slate-50">Updated</th>
                      <th className="px-4 py-3 bg-slate-50">Method</th>
                      <th className="px-4 py-3 bg-slate-50">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {docs.map((d) => {
                      const st = getDocStatus(d);
                      return (
                        <tr key={d._id} className="border-b border-slate-100">
                          <td className="px-4 py-3 text-slate-700">
                            {fmtDateTime(d.createdAt, '-')}
                            <div className="text-xs text-slate-400">{d._id}</div>
                          </td>
                          <td className="px-4 py-3">{statusPill(d)}</td>
                          <td className="px-4 py-3 text-slate-700">{d.itemsParsed ?? 0}</td>
                          <td className="px-4 py-3 text-slate-700">{d.itemsUpdated ?? 0}</td>
                          <td className="px-4 py-3 text-slate-700">{d.parseMeta?.method || '-'}</td>
                          <td className="px-4 py-3 text-slate-700">
                            {st === 'failed' ? getDocError(d) || 'Unknown error' : '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="text-xs text-slate-500">
            Tip: If status stays Pending, backend parsing is still running. If it turns Failed, the error will show here.
          </div>
        </div>
      </Modal>

      {/* Add URL */}
      <Modal isOpen={urlOpen} title="Add Price List URL" onClose={() => setUrlOpen(false)} maxWidthClass="max-w-xl">
        <div className="space-y-4">
          <div className="text-xs text-slate-500">
            This will create a document (source: link) and parse it in background like file upload.
            <br />
            <span className="font-semibold">Vendor must be selected in Filter</span>.
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">URL</label>
            <input
              value={flipbookUrl}
              onChange={(e) => setFlipbookUrl(e.target.value)}
              placeholder="https://..."
              className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm outline-none focus:ring-2 focus:ring-primary-500/30"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Subject (optional)</label>
            <input
              value={flipbookSubject}
              onChange={(e) => setFlipbookSubject(e.target.value)}
              placeholder="Price List (Link)"
              className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm outline-none focus:ring-2 focus:ring-primary-500/30"
            />
          </div>

          <div className="pt-2 flex justify-end gap-2 border-t border-slate-200">
            <button
              onClick={() => setUrlOpen(false)}
              className="px-4 py-2 rounded-md border border-slate-200 bg-white text-sm hover:bg-slate-50"
              disabled={urlSubmitting}
            >
              Cancel
            </button>
            <button
              onClick={onSubmitFlipbookUrl}
              className="px-4 py-2 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600"
              disabled={urlSubmitting}
            >
              {urlSubmitting ? 'Submitting…' : 'Submit URL'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Send */}
      <Modal isOpen={sendOpen} title="Send Price List" onClose={() => setSendOpen(false)} maxWidthClass="max-w-xl">
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">To</label>
            <input
              value={sendTo}
              onChange={(e) => setSendTo(e.target.value)}
              placeholder="Enter To"
              className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm outline-none focus:ring-2 focus:ring-primary-500/30"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">CC</label>
            <input
              value={sendCc}
              onChange={(e) => setSendCc(e.target.value)}
              placeholder="Enter CC"
              className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm outline-none focus:ring-2 focus:ring-primary-500/30"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <VendorSelect
                label="Vendor Name"
                value={sendVendor}
                allowClear={false}
                onChange={(next) => {
                  setSendVendor(next);
                  const picked = vendors.find((x) => x._id === next.vendorId);
                  if (picked) setSendTo(picked.email || '');
                }}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Contact</label>
              <input
                value={sendContact}
                onChange={(e) => setSendContact(e.target.value)}
                placeholder="Enter Contact"
                className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm outline-none focus:ring-2 focus:ring-primary-500/30"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Subject</label>
            <input
              value={sendSubject}
              onChange={(e) => setSendSubject(e.target.value)}
              placeholder="Enter Subject"
              className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm outline-none focus:ring-2 focus:ring-primary-500/30"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Message</label>
            <textarea
              value={sendMessage}
              onChange={(e) => setSendMessage(e.target.value)}
              placeholder="Write here..."
              className="w-full min-h-[120px] px-3 py-2 border border-slate-200 rounded-md text-sm outline-none focus:ring-2 focus:ring-primary-500/30"
            />
          </div>

          <div className="pt-2">
            <button
              onClick={sendPriceList}
              className="w-full h-10 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600"
            >
              SEND PRICE LIST
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
